/**
 * Polling cadences for live-ish UI, in one place.
 *
 * ## Why these numbers are a cost decision, not a taste decision
 *
 * The API runs on Cloud Run with request-based billing, which charges
 * vCPU-seconds for the **wall-clock duration a request is in flight** — not for
 * CPU actually consumed. Two consequences drive everything below:
 *
 * 1. **Frequency costs money.** An open, focused tab polling every 2–3s keeps
 *    the API measurably awake for as long as it is open. Measured on
 *    2026-08-11/13 with no daemon running, an open tab's `useLiveUpdates`
 *    fallback was the single largest source of residual API traffic
 *    (~1,164 requests: epics 388 / features 389 / epic-requests 387 in
 *    lockstep).
 * 2. **Held connections are far worse than polling.** See the SSE note below.
 *
 * TanStack Query's `refetchIntervalInBackground` defaults to `false`, so these
 * intervals pause while the tab is unfocused — the cost only accrues for a tab
 * someone is actually looking at. Combined with `refetchOnWindowFocus: true`
 * (set on the session-events query), a slower interval costs very little
 * perceived freshness: returning to the tab triggers an immediate refetch, so
 * the interval only governs updates while you are already staring at the page.
 *
 * ## ⚠️ SSE is NOT the cheap upgrade it looks like
 *
 * `useLiveUpdates` prefers an SSE stream (`packages/api/src/routes/events.ts`)
 * and falls back to polling. On Cloud Run, an SSE connection is a request held
 * open, so it pins a billable instance for its entire life and defeats
 * `--min-instances=0`. Cloud Run's `--timeout=300` then force-closes it and the
 * client reconnects, producing a ~100% duty cycle — the identical failure that
 * made the daemon's `next-job` long-poll cost ~$64/month in July 2026 (see
 * `deploy/gcp/README.md` § "Scale-to-zero is not automatic").
 *
 * Rough comparison on Cloud Run:
 *   - SSE connected: ~1 instance pinned continuously  -> ~$64/month
 *   - Polling at 60s: ~3 short requests per tick      -> well under $1/month
 *
 * So the polling fallback is the *cheaper* architecture here, and SSE staying
 * disconnected is not a bug to be fixed without first accounting for the bill.
 * Before enabling SSE in production, either move that endpoint somewhere that
 * does not bill wall-clock, or accept the always-on instance cost explicitly.
 */

/**
 * Background freshness for list views when SSE is unavailable
 * (`useLiveUpdates`'s fallback). This ticks for as long as a tab is open, so it
 * is the interval most worth keeping slow.
 */
export const LIST_FALLBACK_POLL_MS = 60_000;

/**
 * Session event streams (activity feed, session monitor). Paired with
 * `refetchOnWindowFocus: true`, so returning to the tab refreshes immediately
 * and this only paces updates while the page is already in view.
 *
 * Event-list only (ENG-370): no progress number is derived from these events.
 * Completed/total counts, stage, percentage and ETA come from `useRunProgress`,
 * which is SSE-first and polls only as a fallback (`RUN_PROGRESS_FALLBACK_POLL_MS`).
 */
export const SESSION_EVENTS_POLL_MS = 15_000;

/**
 * Session monitor — a deliberately closer watch than the activity feed, for a
 * view whose whole purpose is following a run in progress.
 */
export const SESSION_MONITOR_POLL_MS = 10_000;

/**
 * Machine online/offline list. The server's online window is 150s
 * (`MACHINE_ONLINE_THRESHOLD_MS`), so this is comfortably fast enough to render
 * accurate status while polling unconditionally wherever it is mounted.
 */
export const MACHINE_LIST_POLL_MS = 30_000;

/**
 * A live job (interview turn / dispatched execution). Deliberately fast: the
 * user is watching, and `useJob` stops polling entirely once the job reaches a
 * terminal status, so this is bounded by the length of real work rather than by
 * how long a tab stays open.
 *
 * Job state only (turns, status, waiting reason). It is never the source of
 * progress numbers; those come from the job's RunProgress (ENG-370).
 */
export const LIVE_JOB_POLL_MS = 2_000;

/**
 * ENG-389: a live job whose event stream is connected. Replies and turns arrive
 * over SSE (and `job.turn` refetches the job), so polling is only a safety net
 * for missed events and can run much slower than {@link LIVE_JOB_POLL_MS}.
 */
export const STREAMED_JOB_FALLBACK_POLL_MS = 10_000;

/**
 * The epic and request pages' "is a plan/build running for this?" lookup
 * (ENG-E56 follow-up). It only drives a Cancel button, so a slower pace is fine;
 * returning to the tab refreshes it immediately.
 *
 * Not used for progress (ENG-370): the progress text next to the Cancel button
 * comes from `useRunProgress`, so this lookup only decides whether a run exists.
 */
export const ACTIVE_JOB_POLL_MS = 15_000;

/**
 * `useRunProgress` (ENG-367) fallback poll for a single planning/build run's
 * progress, used ONLY while SSE is not connected and only until the run reaches
 * a terminal state (completed/failed/cancelled). Since SSE is usually
 * disconnected on Cloud Run (see above), this is the common path. It is one
 * small request per watched run, so it matches `ACTIVE_JOB_POLL_MS`.
 */
export const RUN_PROGRESS_FALLBACK_POLL_MS = 15_000;
